import React from "react";
import { formatCurrency, formatShortDate } from "../../../utils/format";
import EmptyValue from "../../../components/EmptyValue";
import StatusSelect from "../../../components/StatusSelect";
import RowActions from "../../../components/RowActions";
import { DELIVERY_PARTNER_IDS_MAP } from "../../../../components/delivery/constants";
import { PRESORTED, PRESORTED_ORDER_TYPE, STRIPS_ORDER_TYPE } from "../../../../utils/constants";

// B2B / partner orders — column definitions.
//
// The design asks for 29 columns. One row of GET /api/userOrder/admin fills 20 of them
// with ZERO extra requests: everything below reads either a flattened field from
// toOrderRow or `row.delivery`, which that hook already returns whole.
//
// The other nine are not rendered. That is not an oversight and not a plumbing gap —
// for most of them the data is not captured anywhere in this system (see
// UNAVAILABLE_COLUMNS for the per-column evidence). They are disclosed under the table
// instead of shipped as columns of blanks, the same call the Orders page made.

/** Legacy's clickable-cell treatment (src/components/orderTable/table-layout.jsx:313). */
const LINK_CLASS = "font-sans font-medium text-elm-500 underline hover:text-black cursor-pointer";

/**
 * Row-selection checkbox, copied verbatim from the legacy order table's per-row
 * checkbox (src/components/orderTable/table-layout.jsx:336) and matched by the Orders
 * page's own `CHECKBOX_CLASS`. The blue check comes from @tailwindcss/forms reading
 * `text-blue-600`; the `dark:` variants are legacy's own and are kept so these
 * checkboxes are not the odd ones out next to /all-orders.
 */
const CHECKBOX_CLASS =
    "w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600";

/**
 * SAFETY GUARD — legacy renders the delete button ONLY when the order carries no
 * payment in SUCCESS or PENDING (src/components/quotationsTable/table-instance.jsx:30,
 * and the same filter as a `disabled` on src/components/orderTable/table-instance.jsx:48).
 * Deleting an order money has already moved against is not recoverable from this screen,
 * so the same rule is enforced here before `RowActions` is even given a delete handler.
 *
 * `useOrdersList.toOrderRow` exposes the payments twice — flattened as `row.payments`
 * and untouched under `row.raw.payments`. Either is fine; if NEITHER is an array we
 * cannot prove the order is safe to delete, so we refuse rather than assume "no
 * payments" and offer a destructive button we have not actually checked.
 *
 * @param {object} row
 * @returns {boolean} true only when the order is provably payment-free.
 */
export function canDeleteOrder(row) {
    const payments = row?.raw?.payments ?? row?.payments;
    if (!Array.isArray(payments)) return false;
    return !payments.some((payment) => payment?.status === "SUCCESS" || payment?.status === "PENDING");
}

/**
 * The allowed next statuses for one row, mirroring legacy's per-row choice
 * (src/components/orderTable/table-instance.jsx:338 and
 * src/pages/edit-orders/components/MainFields.jsx:107): a PRESORTED order gets the
 * PRESORTED_ORDERS list, everything else gets STRIPS_ORDERS.
 *
 * `orderStatuses` is whatever `getOrderStatuses()` returned — it is `undefined` until
 * GET /userOrder/admin/allowedStatus resolves, hence the optional chaining and the `[]`
 * floor. StatusSelect renders plain text rather than an empty dropdown for `[]`, so a
 * row shows its status as text for the moment before the list lands.
 *
 * @param {Record<string, string[]> | undefined} orderStatuses
 * @param {object} row
 * @returns {string[]}
 */
function statusOptionsFor(orderStatuses, row) {
    const key = row.orderType?.includes(PRESORTED) ? PRESORTED_ORDER_TYPE : STRIPS_ORDER_TYPE;
    return orderStatuses?.[key] || [];
}

/** Excel cells take scalars: a React node or an em dash placeholder is not one. */
const exportText = (value) => (value === null || value === undefined ? "" : value);

/**
 * DataTable's default cell is `row[key] ?? emptyCell`, and `??` does not catch "".
 * Several of these fields come back as empty strings rather than null on real rows, so
 * anything not already normalised in toOrderRow goes through this.
 */
const text = (value) =>
    value === null || value === undefined || value === "" ? <EmptyValue /> : value;

/** `{ SHIPROCKET: 1, ... }` inverted, so a delivery's partnerId can name itself. */
const PARTNER_NAME_BY_ID = Object.entries(DELIVERY_PARTNER_IDS_MAP).reduce((acc, [name, id]) => {
    acc[id] = name;
    return acc;
}, {});

/**
 * eVitalRX hosts the invoice; nothing stores its URL against the order. The hosted page
 * is addressed by `/invoice/view/<base64(inventoryId)>/` — verified against order 14250
 * (OAMSF2BALZ -> https://www.evitalcare.in/invoice/view/T0FNU0YyQkFMWg==/).
 *
 * This is a DERIVED third-party URL, not a documented contract: if eVitalRX changes its
 * tokenisation or path every link 404s silently. Guarded so a non-ASCII id cannot throw
 * btoa() inside a render.
 */
const INVENTORY_ID_RE = /^[A-Za-z0-9._-]+$/;
export function evitalInvoiceUrl(inventoryId) {
    if (!inventoryId || !INVENTORY_ID_RE.test(inventoryId)) return null;
    return `https://www.evitalcare.in/invoice/view/${btoa(inventoryId)}/`;
}

/**
 * `deliveries.additionalDetails.trackingLink` is the intended field but is NULL on every
 * delivery row in this database — the write landed after they were all created. Fall
 * back to building the URL from the AWB, but ONLY for DTDC: the format is carrier
 * specific, and a link that 404s is worse than an unlinked tracking number.
 */
export function tracking(delivery) {
    if (!delivery) return { url: null, awb: null };
    const courier = delivery.courierDetails || {};
    const awb = delivery.deliveryPartnerUniqueId || courier.awb || courier.reference_number || null;
    const stored = delivery.additionalDetails?.trackingLink || null;
    if (stored) return { url: stored, awb };
    if (awb && Number(delivery.deliveryPartnerId) === DELIVERY_PARTNER_IDS_MAP.DTDC) {
        return { url: `https://www.dtdc.com/track-your-shipment/?awb=${encodeURIComponent(awb)}`, awb };
    }
    return { url: null, awb };
}

/** courierDetails is stored raw per carrier — DTDC snake_case, Shiprocket camelCase. */
export function deliveryPartnerName(delivery) {
    if (!delivery) return null;
    const courier = delivery.courierDetails || {};
    return (
        courier.courier_partner ||
        courier.courierName ||
        PARTNER_NAME_BY_ID[delivery.deliveryPartnerId] ||
        null
    );
}

/**
 * The nine columns the design asks for that this backend cannot fill. Surfaced in the UI
 * as an explicit note — an ops user who has seen the design will look for these, and
 * "not recorded" is a better answer than a column of blanks.
 *
 * Every count below was measured against the live database, not inferred.
 */
export const UNAVAILABLE_COLUMNS = [
    {
        label: "Confirmed On",
        reason:
            "No such timestamp exists. orderStatusHistory is switched on and its endpoint answers, but it holds zero order rows and there is no historical source to backfill from — the TAT read model aliases this to createdAt, i.e. Placed On",
    },
    {
        label: "Delivered On",
        reason:
            "deliveries.deliveredAt is a real field but is unset on all 331 delivery rows, while 11,269 orders already sit in DELIVERED. It will populate when a carrier webhook first completes a delivery",
    },
    {
        label: "Cancelled On",
        reason: "Not recorded — no cancellation timestamp on the order, and orderStatusHistory has no order rows",
    },
    {
        label: "Cancelled By",
        reason: "Not recorded — the actor column exists on orderStatusHistory but is null on every row written so far",
    },
    {
        label: "Wallet Covered Amount",
        reason:
            "Nothing to read: no wallet column on any table, no WALLET or COIN_REDEEM payments, and the coins ledger is not tied to an order",
    },
    {
        label: "PillUp Cancel Comment",
        reason:
            "There is only one cancellation free-text field (cancelledReason), which the Cancel Comment column already writes. The design's second dropdown has no separate column behind it",
    },
    {
        label: "Remarks",
        reason: "No second free-text field on the order beyond Notes and the cancellation reason",
    },
    {
        label: "Substitute",
        reason:
            "Only reachable one order at a time (GET /userOrder/admin/orderDetails/:id), which would cost 20 extra requests per page and misses substitutions recorded against the quotation. Needs a backend include before it can be a column",
    },
    {
        label: "Substitute Medicine",
        reason: "Same source as Substitute — per-order only, and medicine names need a further lookup per substitution",
    },
];

/**
 * The design shows two cancel dropdowns, but both write the same column, so their option
 * lists are merged here into one honest control. Order preserved from the design.
 */
export const CANCEL_REASONS = [
    "",
    "User requested cancellation",
    "Payment pending",
    "Out of stock",
    "Address not serviceable",
    "Duplicate order",
    "Cancelled within SLA",
    "Delivery failed",
    "Rx validation failed",
    "No response from user",
];

/**
 * @param {object} handlers
 * @param {string|number|null} handlers.savingId order id currently being written. Disables
 *   BOTH row-level writes (status and cancel comment) so a row can only have one save in
 *   flight at a time.
 * @param {(row: object, reason: string) => void} handlers.onReasonChange
 * @param {Record<string, string[]> | undefined} handlers.orderStatuses the whole
 *   `getOrderStatuses()` map, keyed by order type. It arrives from a HOOK, so the page
 *   calls that helper at its top level and threads the result in here — never inside a
 *   cell renderer, which runs mid-render of the parent where hooks cannot live.
 * @param {(row: object, status: string) => void} handlers.onStatusChange
 * @param {Set<string>} handlers.selectedIds selected order ids, as strings
 * @param {(id: string) => void} handlers.onToggleRow
 * @param {(row: object) => void} handlers.onEdit
 * @param {(row: object) => void} handlers.onDelete invoked only after RowActions'
 *   confirm, and only for rows this file's `canDeleteOrder` guard allows.
 */
export function buildB2BColumns({
    savingId,
    onReasonChange,
    orderStatuses,
    onStatusChange,
    selectedIds,
    onToggleRow,
    onEdit,
    onDelete,
}) {
    const stop = (event) => event.stopPropagation();

    return [
        {
            // Legacy's leading select column, header and all: its <th> is literally "#"
            // (src/components/orderTable/Constants.js:4) because select-all lives in the
            // toolbar above the table, not in the header cell. Kept that way here so
            // there is exactly one select-all control on the page rather than two that
            // do the same thing.
            key: "select",
            label: "#",
            // Selection is a UI concern; it is not a column of the exported sheet.
            excludeFromExport: true,
            render: (row) => (
                <input
                    type="checkbox"
                    aria-label={`Select order ${row.orderId}`}
                    checked={selectedIds.has(String(row.orderId))}
                    onClick={stop}
                    onChange={() => onToggleRow(String(row.orderId))}
                    className={CHECKBOX_CLASS}
                />
            ),
        },
        {
            key: "orderId",
            label: "Order ID",
            exportValue: (row) => exportText(row.orderId),
            render: (row) => (
                <span className="whitespace-nowrap font-sans font-medium text-elm-500">{row.orderId}</span>
            ),
        },
        {
            key: "partnerName",
            label: "B2B Partner",
            exportValue: (row) => exportText(row.partnerName),
            render: (row) => text(row.partnerName),
        },
        {
            key: "prescription",
            label: "Prescription PDF",
            // The sheet gets the URL itself — a cell reading "Open" is useless offline.
            exportValue: (row) => row.prescriptionLinks?.[0] || "",
            render: (row) =>
                row.prescriptionLinks?.length ? (
                    <a
                        href={row.prescriptionLinks[0]}
                        target="_blank"
                        rel="noreferrer"
                        onClick={stop}
                        className={LINK_CLASS}
                    >
                        Open
                    </a>
                ) : (
                    <EmptyValue />
                ),
        },
        {
            key: "patientName",
            label: "User Name",
            exportValue: (row) => exportText(row.patientName),
            render: (row) => text(row.patientName),
        },
        {
            key: "contactNumber",
            label: "User Mobile",
            exportValue: (row) => exportText(row.contactNumber || row.raw?.address?.phone),
            render: (row) => text(row.contactNumber || row.raw?.address?.phone),
        },
        {
            key: "pincode",
            label: "Pincode",
            exportValue: (row) => exportText(row.pincode),
            render: (row) => text(row.pincode),
        },
        {
            key: "state",
            label: "State",
            exportValue: (row) => exportText(row.state),
            render: (row) => text(row.state),
        },
        {
            key: "city",
            label: "City",
            exportValue: (row) => exportText(row.city),
            render: (row) => text(row.city),
        },
        {
            key: "address",
            label: "Address",
            wrap: true,
            exportValue: (row) => exportText(row.address),
            render: (row) =>
                row.address ? (
                    <span className="block max-w-[220px] whitespace-normal">{row.address}</span>
                ) : (
                    <EmptyValue />
                ),
        },
        {
            key: "status",
            label: "Order Status",
            // The sheet keeps the raw enum value, which is what the API stores and what
            // anyone reconciling this export against the database will match on.
            exportValue: (row) => exportText(row.status),
            // Was a read-only StatusBadge. It is now the legacy inline <select>: ops
            // change a status from the row, exactly as on /all-orders and /quotations.
            // The trade-off is deliberate — the dropdown's options are the API's raw
            // status codes, so the cell shows the raw code rather than the badge's
            // friendlier label. A friendly label in the closed select with raw codes
            // inside it would be worse: you could not tell which option you were on.
            render: (row) => (
                <StatusSelect
                    value={row.status}
                    options={statusOptionsFor(orderStatuses, row)}
                    // One write per row at a time — the same `savingId` that disables the
                    // cancel-comment select below, so the two can never race each other
                    // on the same order.
                    disabled={savingId === row.orderId}
                    onChange={(next) => onStatusChange(row, next)}
                />
            ),
        },
        {
            key: "orderDate",
            label: "Placed On",
            exportValue: (row) => (row.orderDate ? formatShortDate(row.orderDate) : ""),
            render: (row) => formatShortDate(row.orderDate),
        },
        {
            key: "cancelledReason",
            label: "Cancel Comment",
            exportValue: (row) => exportText(row.cancelledReason),
            render: (row) => {
                const value = row.cancelledReason || "";
                // cancelledReason is free text server-side, so a stored value need not be
                // in this list. A controlled <select> whose value matches no <option>
                // renders BLANK — the row would look unset, and the next change would
                // silently overwrite a real reason. Carry the stored value in as an
                // extra option so it is visible and survives an unrelated edit.
                const options = CANCEL_REASONS.includes(value) ? CANCEL_REASONS : [...CANCEL_REASONS, value];
                return (
                    <select
                        value={value}
                        disabled={savingId === row.orderId}
                        onClick={stop}
                        onChange={(event) => onReasonChange(row, event.target.value)}
                        className="min-w-[190px] rounded-lg border border-gray-300 px-3 py-2 text-sm font-sans text-black focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {options.map((reason) => (
                            <option key={reason || "none"} value={reason}>
                                {reason || "Select reason"}
                            </option>
                        ))}
                    </select>
                );
            },
        },
        {
            key: "orderType",
            label: "Order Type",
            exportValue: (row) => exportText(row.orderType),
            render: (row) => text(row.orderType),
        },
        {
            key: "total",
            label: "Invoice Amount",
            align: "right",
            // Raw number, not the "₹1,23,457" display string: Excel can sum a number and
            // cannot sum a currency-formatted string.
            exportValue: (row) => (row.total === null || row.total === undefined ? "" : row.total),
            render: (row) =>
                row.total === null || row.total === undefined ? (
                    <EmptyValue />
                ) : (
                    <span className="tabular-nums">{formatCurrency(row.total)}</span>
                ),
        },
        {
            key: "shippingCharges",
            label: "Delivery Charge",
            align: "right",
            exportValue: (row) =>
                row.shippingCharges === null || row.shippingCharges === undefined ? "" : row.shippingCharges,
            render: (row) =>
                row.shippingCharges === null || row.shippingCharges === undefined ? (
                    <EmptyValue />
                ) : (
                    <span className="tabular-nums">{formatCurrency(row.shippingCharges)}</span>
                ),
        },
        {
            key: "codAmount",
            label: "COD Amount",
            align: "right",
            exportValue: (row) => {
                const pkg = row.delivery?.packageDetails;
                return pkg?.isCod ? pkg.codAmount : "";
            },
            render: (row) => {
                const pkg = row.delivery?.packageDetails;
                if (!pkg || !pkg.isCod) return <EmptyValue />;
                return <span className="tabular-nums">{formatCurrency(pkg.codAmount)}</span>;
            },
        },
        {
            key: "invoicePdf",
            label: "Invoice PDF",
            exportValue: (row) => evitalInvoiceUrl(row.inventoryId) || "",
            render: (row) => {
                const url = evitalInvoiceUrl(row.inventoryId);
                return url ? (
                    <a href={url} target="_blank" rel="noreferrer" onClick={stop} className={LINK_CLASS}>
                        Open
                    </a>
                ) : (
                    <EmptyValue />
                );
            },
        },
        {
            key: "trackingLink",
            label: "Tracking Link",
            // Same order of preference as the cell: a real URL, else the bare AWB.
            exportValue: (row) => {
                const { url, awb } = tracking(row.delivery);
                return url || awb || "";
            },
            render: (row) => {
                const { url, awb } = tracking(row.delivery);
                if (url) {
                    return (
                        <a href={url} target="_blank" rel="noreferrer" onClick={stop} className={LINK_CLASS}>
                            Track
                        </a>
                    );
                }
                // An AWB with no known URL format is still useful to an ops user; a link
                // that 404s is not.
                return awb ? <span className="font-sans text-black">{awb}</span> : <EmptyValue />;
            },
        },
        {
            key: "deliveryPartner",
            label: "Delivery Partner",
            exportValue: (row) => deliveryPartnerName(row.delivery) || "",
            render: (row) => text(deliveryPartnerName(row.delivery)),
        },
        {
            key: "notes",
            label: "Notes",
            wrap: true,
            exportValue: (row) => exportText(row.notes),
            render: (row) =>
                row.notes ? <span className="block max-w-[260px] whitespace-normal">{row.notes}</span> : <EmptyValue />,
        },
        // Not in the design's list, but kept: partnerEntityId is the partner's OWN
        // reference for the order and the natural key when reconciling with them.
        {
            key: "partnerEntityId",
            label: "Partner Order ID",
            exportValue: (row) => exportText(row.partnerEntityId),
            render: (row) => text(row.partnerEntityId),
        },
        {
            // Legacy's Action column, always last. Buttons are RowActions, which owns
            // the verbatim legacy markup, the confirm() prompt and stopPropagation.
            key: "actions",
            label: "Action",
            // Buttons are not data; the sheet ends at Partner Order ID.
            excludeFromExport: true,
            render: (row) => (
                <RowActions
                    // See canDeleteOrder: no delete button at all for an order with a
                    // SUCCESS or PENDING payment, matching legacy.
                    canDelete={canDeleteOrder(row)}
                    onEdit={() => onEdit(row)}
                    onDelete={() => onDelete(row)}
                />
            ),
        },
    ];
}

/**
 * Build the Excel payload from the SAME column list the table renders, so a column added
 * above is exported without a second edit and the sheet's headers can never drift from
 * the screen's. Columns flagged `excludeFromExport` (the leading checkbox and the trailing
 * Action buttons) are dropped — neither is data.
 *
 * `DownLoadTable(fileName, sheetName, headers, rows)` wants a flat array of header strings
 * and an array of scalar rows, which is why each column carries `exportValue` rather than
 * the export trying to stringify the rendered React node.
 *
 * @param {Array<object>} columns the array returned by buildB2BColumns
 * @param {Array<object>} rows the order rows to export
 * @returns {{ headers: string[], data: Array<Array<string|number>> }}
 */
export function buildB2BExport(columns, rows) {
    const exportable = columns.filter((column) => !column.excludeFromExport);
    return {
        headers: exportable.map((column) => column.label),
        data: rows.map((row) =>
            exportable.map((column) => (column.exportValue ? column.exportValue(row) : exportText(row[column.key])))
        ),
    };
}
